'use client'

import { type ReactNode } from 'react'

import AppIcon from '@/components/content/app/AppIcon'
import PrimaryButton from '@/components/content/app/PrimaryButton'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import BestCta from '@/components/content/best/shared/BestCta'
import ExternalIndicator from '@/components/ui/ExternalIndicator'
import Link from '@/components/ui/Link'
import Menu from '@/components/ui/Menu'
import Pill from '@/components/ui/Pill'
import {
  determinePremiumPartner,
  getAppRecommendation,
  getPrimaryButton,
} from '@/components/content/app/helpers'
import { determineDealsToDisplay, formatDealButton } from '@/components/content/deals/helpers'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { checkAppBranding, processAffiliateLink } from '@/lib/formatting'
import { createLogger } from '@/lib/logger'
import { cn, getBrandColors, trimUrl } from '@/lib/utils'
import type { MenuItem } from '@/helpers/menus'
import type { BestCtaData } from '@/app/(app)/compare/[slug]/client-fetchers'
import type { App, Category, Deal, Link as LinkType } from '@/payload-types'

const logger = createLogger('CompareSidebar')

type CompareSidebarProps = {
  apps: App[]
  menu: MenuItem[]
  menuByAppId: Partial<Record<App['id'], MenuItem[]>>
  category?: Category
  bestCta?: BestCtaData | null
}

// `apps` arrives winner-first; only the winner gets the primary CTA.
export default function CompareSidebar({
  apps,
  menu,
  menuByAppId,
  category,
  bestCta,
}: CompareSidebarProps): ReactNode {
  return (
    <div className="flex w-full flex-row items-center md:flex-col md:items-start">
      <div className="flex w-full min-w-0 flex-1 items-center gap-1.5 md:flex-col md:items-start md:gap-0 md:divide-y md:divide-gray-200">
        {apps.map((app, index) => (
          <CompareSidebarApp
            key={app.id}
            rank={index + 1}
            app={app}
            menu={menuByAppId[app.id] ?? menu}
            category={category}
            isWinner={index === 0}
          />
        ))}
        <Breadcrumb placeholder="Comparison" />
      </div>
      {bestCta && (
        <BestCta {...bestCta} className="hidden w-full border-t border-gray-200 p-4 md:flex" />
      )}
      <div className="flex shrink-0 md:hidden">
        <Menu items={menu} align="right" subAlign="down" />
      </div>
    </div>
  )
}

type CompareSidebarAppProps = {
  rank: number
  app: App
  menu: MenuItem[]
  category?: Category
  isWinner: boolean
}

function CompareSidebarApp({
  rank,
  app,
  menu,
  category,
  isWinner,
}: CompareSidebarAppProps): ReactNode {
  const { backgroundColor } = getBrandColors(app)
  const { appIcon, appGlyph } = checkAppBranding(app)

  const appDeals = app.deals?.docs as Deal[]
  const formattedDeals = determineDealsToDisplay(appDeals)
  const hasDeals = formattedDeals?.length > 0
  const primaryDeal = formattedDeals[0]

  const affiliate = app.slug
    ? processAffiliateLink(app.affiliateLink as LinkType, app.slug, app.name, app.partnershipTier)
    : null

  if (!affiliate) {
    logger.error(`Missing slug for app ${app.id} in CompareSidebar`)
    return null
  }

  const handleClick = (): void => {
    if (hasDeals) {
      localStorage.setItem('efficient:claim', 'true')
    }
  }

  const AffiliateLink = ({
    className,
    children,
  }: {
    className?: string
    children: ReactNode
  }): ReactNode => (
    <Link
      href={affiliate.url}
      target="_blank"
      passReferrerUrl={affiliate.passReferrerUrl}
      sponsored={affiliate.isSponsored}
      onClick={handleClick}
      className={className}
    >
      {children}
    </Link>
  )

  const dealLabel = primaryDeal ? formatDealButton(primaryDeal) : undefined

  const { recommendation } = getAppRecommendation({ app, categoryId: category?.id ?? 0 })

  // Not a summary CTA: the summary fallback links to /compare, which is circular here,
  // so non-partners get "Read review" instead.
  const primaryButtonOptions = {
    app,
    deal: { count: formattedDeals?.length, label: dealLabel },
    isBest: recommendation === Recommendation.TopPick,
    isRecommended:
      recommendation !== Recommendation.Capable && recommendation !== Recommendation.NotRecommended,
    isBroadCategory: Boolean(category?.isBroadCategory),
    desktopIsRequired: primaryDeal?.desktopIsRequired ?? false,
  }
  const primaryButton = getPrimaryButton({ ...primaryButtonOptions, isDesktop: true })
  const primaryButtons = primaryDeal?.desktopIsRequired
    ? [
        {
          button: getPrimaryButton({ ...primaryButtonOptions, isDesktop: false }),
          visibility: 'lg:hidden',
        },
        { button: primaryButton, visibility: 'hidden lg:flex' },
      ]
    : [{ button: primaryButton, visibility: undefined }]

  // Icon and name follow the primary CTA; only the website URL is always the affiliate link
  const PrimaryLink = ({
    className,
    children,
  }: {
    className?: string
    children: ReactNode | ((button: ReturnType<typeof getPrimaryButton>) => ReactNode)
  }): ReactNode =>
    primaryButtons.map(({ button, visibility }, index) => (
      <PrimaryButton
        key={index}
        primaryButton={button}
        primaryDeal={primaryDeal}
        app={app}
        onClick={handleClick}
        className={cn(className, visibility)}
      >
        {typeof children === 'function' ? children(button) : children}
      </PrimaryButton>
    ))

  return (
    <div className="flex shrink-0 flex-col gap-3.5 last-of-type:pr-2 md:w-full md:p-4 md:last-of-type:pr-4">
      <div className="flex items-center justify-between md:w-full">
        <div className="flex min-w-0 items-center gap-3.5">
          <div className="hidden h-7 w-7 items-center justify-center rounded-full border border-gray-200 font-mono text-12 text-gray-900 md:flex">
            {rank}
          </div>
          <PrimaryLink className="h-8 w-8 shrink-0 md:h-14 md:w-14">
            <AppIcon
              glyph={appGlyph}
              icon={appIcon}
              name={app.name}
              background={backgroundColor}
              transition={false}
            />
          </PrimaryLink>
          <div className="hidden min-w-0 flex-col gap-0.5 leading-tight md:mt-1 md:flex">
            <PrimaryLink className="truncate font-semibold text-gray-950 dark:text-white">
              {app.name}
            </PrimaryLink>

            <div className="group inline-flex items-center">
              <AffiliateLink className="underlined max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-13 text-gray-600 dark:text-zinc-500">
                {trimUrl(app.websiteUrl)}
              </AffiliateLink>
              <ExternalIndicator />
            </div>
          </div>
        </div>
        {!isWinner && (
          <div className="hidden shrink-0 md:flex">
            <Menu items={menu} align="right" subAlign="down" />
          </div>
        )}
      </div>
      {isWinner && (
        <div className="hidden gap-2.5 md:flex md:w-full">
          <PrimaryLink className="flex w-full">
            {(button) => (
              <Pill
                style={determinePremiumPartner(app.partnershipTier) ? 'primary' : 'outline'}
                className="group md:w-full md:justify-center"
              >
                {button.label}
                {button.opensInNewTab && <ExternalIndicator />}
              </Pill>
            )}
          </PrimaryLink>

          <div className="shrink-0">
            <Menu items={menu} align="right" subAlign="down" />
          </div>
        </div>
      )}
    </div>
  )
}
